/**
 * Factory Schema.org: costruisce oggetti JSON-LD dati i modelli
 * dell'app (school, product, article, category, city).
 * Nessun rendering visivo — solo dati per i motori di ricerca.
 */

const SITE = "https://permanina.it";
const ORG = {
  "@type": "Organization",
  name: "Per manina",
  url: SITE,
  logo: `${SITE}/logo.png`,
  sameAs: [
    "https://www.instagram.com/permanina",
  ],
};

/* ------------------------- SCHOOL ------------------------- */
export function schoolSchema(school, reviews = []) {
  if (!school) return null;
  const url = `${SITE}/scuola/${school.id}`;
  const items = [];

  // Preschool (specializzazione di EducationalOrganization + LocalBusiness)
  const preschool = {
    "@context": "https://schema.org",
    "@type": "Preschool",
    "@id": `${url}#preschool`,
    name: school.name,
    url,
    address: {
      "@type": "PostalAddress",
      streetAddress: school.address,
      addressLocality: school.city,
      addressRegion: school.region,
      postalCode: school.zip || undefined,
      addressCountry: "IT",
    },
    telephone: school.phone || undefined,
    email: school.email || undefined,
    sameAs: school.website || undefined,
    additionalType:
      school.type === "pubblica"
        ? "https://schema.org/GovernmentOrganization"
        : "https://schema.org/EducationalOrganization",
    isAccessibleForFree: school.type === "pubblica" ? true : undefined,
    areaServed: school.city,
  };
  if (school.avg_rating && school.review_count > 0) {
    preschool.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(school.avg_rating).toFixed(1),
      reviewCount: school.review_count,
      bestRating: 5,
      worstRating: 1,
    };
  }
  const reviewNodes = (reviews || []).slice(0, 10).map((r) => ({
    "@type": "Review",
    reviewRating: {
      "@type": "Rating",
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
    author: { "@type": "Person", name: r.user_name || "Anonimo" },
    reviewBody: r.comment,
    datePublished: r.created_at,
  }));
  if (reviewNodes.length) preschool.review = reviewNodes;
  items.push(preschool);

  // Breadcrumb
  items.push(_breadcrumb([
    { name: "Home", url: `${SITE}/` },
    { name: "Scuole", url: `${SITE}/cerca` },
    { name: school.city, url: `${SITE}/migliori/${_slug(school.city)}` },
    { name: school.name, url },
  ]));
  return items;
}

/* ------------------------- PRODUCT ------------------------- */
export function productSchema(product, reviews = []) {
  if (!product) return null;
  const url = `${SITE}/prodotto/${product.slug}`;
  const items = [];

  const typeMap = { libro: "Book", gioco: "Product", app: "SoftwareApplication" };
  const baseType = typeMap[product.kind] || "Product";
  const p = {
    "@context": "https://schema.org",
    "@type": baseType,
    "@id": `${url}#product`,
    name: product.name,
    url,
    description: product.description,
    image: product.image_url || `${SITE}/logo.png`,
    brand: product.brand_or_author ? { "@type": "Brand", name: product.brand_or_author } : undefined,
    category: product.category,
    audience: {
      "@type": "PeopleAudience",
      suggestedMinAge: product.age_min,
      suggestedMaxAge: product.age_max,
    },
  };
  if (baseType === "Book" && product.brand_or_author) {
    p.author = { "@type": "Person", name: product.brand_or_author };
  }
  if (product.avg_rating && product.review_count > 0) {
    p.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(product.avg_rating).toFixed(1),
      reviewCount: product.review_count,
      bestRating: 5,
      worstRating: 1,
    };
  }
  if (product.store_link) {
    p.offers = {
      "@type": "Offer",
      url: product.store_link,
      availability: "https://schema.org/InStock",
      priceCurrency: "EUR",
    };
  }
  const reviewNodes = (reviews || []).slice(0, 10).map((r) => ({
    "@type": "Review",
    reviewRating: {
      "@type": "Rating",
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
    author: { "@type": "Person", name: r.user_name || "Anonimo" },
    reviewBody: r.comment,
    datePublished: r.created_at,
  }));
  if (reviewNodes.length) p.review = reviewNodes;
  items.push(p);

  items.push(_breadcrumb([
    { name: "Home", url: `${SITE}/` },
    { name: "Libri & Giochi", url: `${SITE}/prodotti` },
    { name: product.category, url: `${SITE}/prodotti/${product.category}` },
    { name: product.name, url },
  ]));
  return items;
}

/* ------------------------- BLOG ARTICLE ------------------------- */
export function articleSchema(article) {
  if (!article) return null;
  const url = `${SITE}/blog/${article.slug}`;
  const desc = article.trigger || article.title;
  return [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      "@id": `${url}#article`,
      headline: article.title,
      description: desc,
      image: article.image_url ? [article.image_url] : [`${SITE}/logo.png`],
      datePublished: article.published_at || undefined,
      dateModified: article.published_at || undefined,
      author: {
        "@type": "Organization",
        name: "Papà dito · Per manina",
        url: `${SITE}/blog`,
      },
      publisher: ORG,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      keywords: (article.tags || []).join(", ") || undefined,
      wordCount: _articleWordCount(article),
      inLanguage: "it-IT",
      articleSection: "Genitorialità",
    },
    _breadcrumb([
      { name: "Home", url: `${SITE}/` },
      { name: "Papà dito", url: `${SITE}/blog` },
      { name: article.title, url },
    ]),
  ];
}

function _articleWordCount(a) {
  const parts = [a.trigger, a.perche];
  (a.strategie || []).forEach((s) => { parts.push(s.titolo); parts.push(s.testo); });
  if (a.widget) parts.push(a.widget.testo);
  return parts.filter(Boolean).join(" ").split(/\s+/).length;
}

/* ------------------------- PRODUCT CATEGORY ------------------------- */
export function productCategorySchema(cat, products = []) {
  if (!cat) return null;
  const url = `${SITE}/prodotti/${cat.slug}`;
  const items = products.slice(0, 12).map((p, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    url: `${SITE}/prodotto/${p.slug}`,
    name: p.name,
  }));
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${url}#collection`,
      name: `${cat.label} — libri e giochi per bambini`,
      url,
      description: cat.description || `Selezione di libri, giochi e app educative sul tema: ${cat.label}.`,
      isPartOf: { "@type": "WebSite", url: SITE, name: "Per manina" },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: items,
        numberOfItems: products.length,
      },
    },
    _breadcrumb([
      { name: "Home", url: `${SITE}/` },
      { name: "Libri & Giochi", url: `${SITE}/prodotti` },
      { name: cat.label, url },
    ]),
  ];
}

/* ------------------------- HOME / WEBSITE ------------------------- */
export function homeSchema() {
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE}#website`,
      url: SITE,
      name: "Per manina",
      inLanguage: "it-IT",
      publisher: ORG,
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE}/cerca?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    { "@context": "https://schema.org", ...ORG },
  ];
}

/* ------------------------- CITY (TOP BY CITY) ------------------------- */
export function cityTopSchema(city, region, province, slug, list = []) {
  if (!city) return null;
  const url = `${SITE}/migliori/${slug}`;
  const items = list.slice(0, 20).map((s, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    url: `${SITE}/scuola/${s.id}`,
    name: s.name,
  }));
  return [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${url}#collection`,
      name: `Le migliori scuole dell'infanzia a ${city}`,
      url,
      about: {
        "@type": "Place",
        name: city,
        address: {
          "@type": "PostalAddress",
          addressLocality: city,
          addressRegion: region,
          addressCountry: "IT",
        },
      },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: items,
        numberOfItems: list.length,
      },
    },
    _breadcrumb([
      { name: "Home", url: `${SITE}/` },
      { name: "Migliori scuole", url: `${SITE}/migliori` },
      { name: city, url },
    ]),
  ];
}

/* ------------------------- BREADCRUMB ------------------------- */
function _breadcrumb(steps) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: steps.map((s, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: s.name,
      item: s.url,
    })),
  };
}

function _slug(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
